import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { IYbugApi, IYbugSettings } from 'types';
import * as Sentry from '@sentry/react';
import { isITAREnvironment } from 'constants/env.ts';

interface IContextProps {
  yBugApi: IYbugApi | null;
}

const YBugContext = createContext<IContextProps | undefined>(undefined);

interface ProviderProps extends PropsWithChildren {
  url?: string;
  settings: IYbugSettings;
}

export const YBugProvider = ({ url, settings, children }: ProviderProps) => {
  const [yBugApi, setYBugApi] = useState<IYbugApi | null>(null);

  useEffect(() => {
    if (!settings.id) {
      // Return a cleanup function
      return () => {};
    }

    if (isITAREnvironment()) {
      Sentry.captureException(
        'An attempt to access YBug from the ITAR environment. Initialization has been prevented.'
      );
      // Return a cleanup function
      return () => {};
    }

    // Set global YBug settings
    window.ybug_settings = {
      ...settings,
      hide_launcher: true,
      id: settings.id,
      onload: () => {
        setYBugApi(window.Ybug);
        settings.onload?.();
      },
    };

    // Create and append the YBug script
    const script = document.createElement('script');
    script.defer = true;
    script.src = url || `https://widget.ybug.io/button/${settings.id}.js`;
    if (settings?.nonce) {
      script.nonce = settings.nonce; // Optional: Add nonce for CSP
    }
    document.head.append(script);

    // Cleanup function
    return () => {
      if (window.Ybug) {
        try {
          window.Ybug.destroy();
        } catch (e) {
          Sentry.captureException(e);
        }
      }
      script.remove();
      delete window.ybug_settings;
    };
  }, [settings.id]);

  const contextValue = useMemo(() => ({ yBugApi }), [yBugApi]);

  return <YBugContext.Provider value={contextValue}>{children}</YBugContext.Provider>;
};

export const useYBugApi = () => {
  const context = useContext(YBugContext);
  if (!context) {
    throw new Error('Attempted to access yBugApi Context with missing Provider');
  }
  return context.yBugApi;
};
